import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useInteractionState } from '@/hooks';
import 'leaflet/dist/leaflet.css';

type Coordinates = {
  latitude: number;
  longitude: number;
};

type LocationState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; coords: Coordinates }
  | { status: 'error'; message: string }
  | { status: 'denied' };

/**
 * Haversine formula to calculate the distance between two coordinates in kilometers.
 */
function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getCurrentPosition(): Promise<Coordinates> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            reject(new Error('denied'));
            break;
          case err.POSITION_UNAVAILABLE:
            reject(new Error('Position unavailable'));
            break;
          case err.TIMEOUT:
            reject(new Error('Position request timed out'));
            break;
          default:
            reject(new Error('Unknown error'));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  });
}

// Fix default marker icon issue with bundlers
// https://github.com/PaulLeCam/react-leaflet/issues/453
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const restaurantIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const userIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

type MapBoundsUpdaterProps = {
  restCoords: Coordinates | null;
  userCoords: Coordinates | null;
};

/** Marker wrapper that zooms to its position on click */
function ClickZoomMarker({
  position,
  icon,
  zoom = 16,
  children,
}: {
  position: [number, number];
  icon: L.Icon;
  zoom?: number;
  children?: React.ReactNode;
}) {
  const map = useMap();
  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        click: () => {
          map.flyTo(position, zoom, { duration: 1 });
        },
      }}
    >
      {children}
    </Marker>
  );
}

/** Auto-fit the map to show all markers */
function MapBoundsUpdater({ restCoords, userCoords }: MapBoundsUpdaterProps) {
  const map = useMap();
  useEffect(() => {
    const points: [number, number][] = [];
    if (restCoords) points.push([restCoords.latitude, restCoords.longitude]);
    if (userCoords) points.push([userCoords.latitude, userCoords.longitude]);
    if (points.length > 0) {
      map.fitBounds(points, { padding: [50, 50], maxZoom: 16 });
    }
  }, [map, restCoords, userCoords]);
  return null;
}

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: '20px',
  left: '20px',
  zIndex: 1000,
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  padding: '16px 20px',
  borderRadius: '8px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
  maxWidth: '320px',
  pointerEvents: 'auto',
};

const navigateBtnStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '8px 18px',
  marginTop: '10px',
  textDecoration: 'none',
  color: '#fff',
  backgroundColor: '#96e16b',
  borderRadius: '4px',
  cursor: 'pointer',
  border: 'none',
  fontSize: '14px',
  fontWeight: 600,
};

export const MapComponent = () => {
  const { management } = useInteractionState();
  const [locationState, setLocationState] = useState<LocationState>({
    status: 'idle',
  });
  const intl = useIntl();

  const company = management?.company;
  const restLat = company?.latitude;
  const restLng = company?.longitude;
  const restCoords =
    restLat != null && restLng != null && !isNaN(restLat) && !isNaN(restLng)
      ? ({ latitude: restLat, longitude: restLng } as Coordinates)
      : null;
  const hasRestaurantCoords = restCoords !== null;

  const userCoords =
    locationState.status === 'success' ? locationState.coords : null;

  // Auto-request location when the component mounts and restaurant coords exist
  useEffect(() => {
    if (hasRestaurantCoords && locationState.status === 'idle') {
      setLocationState({ status: 'loading' });
      getCurrentPosition()
        .then((coords) => {
          setLocationState({ status: 'success', coords });
        })
        .catch((err: Error) => {
          const message = err.message;
          if (message === 'denied') {
            setLocationState({ status: 'denied' });
          } else {
            setLocationState({ status: 'error', message });
          }
        });
    }
  }, [hasRestaurantCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  const distance =
    userCoords && restCoords
      ? haversineDistance(
          userCoords.latitude,
          userCoords.longitude,
          restCoords.latitude,
          restCoords.longitude
        )
      : null;

  const defaultCenter: [number, number] = restCoords
    ? [restCoords.latitude, restCoords.longitude]
    : [48.1351, 11.582];

  const distanceText =
    distance !== null
      ? distance < 1
        ? `${(distance * 1000).toFixed(0)} m`
        : `${distance.toFixed(1)} km`
      : null;

  const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!restCoords) return;
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      e.preventDefault();
      window.open(
        `maps://app?daddr=${restCoords.latitude},${
          restCoords.longitude
        }&q=${encodeURIComponent(company?.name ?? '')}`,
        '_blank'
      );
      setTimeout(() => {
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${restCoords.latitude},${restCoords.longitude}`,
          '_blank'
        );
      }, 500);
    } else if (userAgent.includes('android')) {
      e.preventDefault();
      window.open(
        `geo:${restCoords.latitude},${restCoords.longitude}?q=${
          restCoords.latitude
        },${restCoords.longitude}(${encodeURIComponent(company?.name ?? '')})`,
        '_blank'
      );
    }
  };

  return (
    <section id="map" className="description_content">
      <div className="text-content container" style={{ padding: '0' }}>
        <div style={{ position: 'relative' }}>
          {hasRestaurantCoords ? (
            <>
              {/* Info overlay on the map */}
              <div style={overlayStyle}>
                {company && (
                  <>
                    <h2
                      style={{
                        margin: '0 0 4px',
                        fontSize: '18px',
                        fontWeight: 700,
                      }}
                    >
                      {company.name}
                    </h2>
                    {company.branch && (
                      <p
                        style={{
                          margin: '0 0 2px',
                          fontSize: '14px',
                          color: '#555',
                        }}
                      >
                        {company.branch}
                      </p>
                    )}
                    <p
                      style={{
                        margin: '0 0 2px',
                        fontSize: '13px',
                        color: '#555',
                      }}
                    >
                      {company.address}
                    </p>
                    <p
                      style={{
                        margin: '0 0 2px',
                        fontSize: '13px',
                        color: '#555',
                      }}
                    >
                      {company.postal ? `${company.postal}, ` : ''}
                      {company.city}
                    </p>
                  </>
                )}

                {/* Loading indicator */}
                {locationState.status === 'loading' && (
                  <p
                    style={{
                      margin: '8px 0 0',
                      fontSize: '13px',
                      color: '#888',
                    }}
                  >
                    <em>
                      <FormattedMessage id="app.map.getLocation" />
                      ...
                    </em>
                  </p>
                )}

                {/* Distance + Navigate when location is available */}
                {locationState.status === 'success' && distanceText && (
                  <div style={{ marginTop: '10px' }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#333',
                      }}
                    >
                      <FormattedMessage id="app.map.distance" />: {distanceText}
                    </p>
                    <a
                      style={navigateBtnStyle}
                      href={`https://www.google.com/maps/dir/?api=1&origin=${
                        userCoords!.latitude
                      },${userCoords!.longitude}&destination=${
                        restCoords!.latitude
                      },${restCoords!.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleNavigate}
                    >
                      <FormattedMessage id="app.map.navigate" />
                    </a>
                  </div>
                )}

                {/* Navigate only when location was denied/error */}
                {(locationState.status === 'denied' ||
                  locationState.status === 'error') && (
                  <div style={{ marginTop: '10px' }}>
                    <a
                      style={navigateBtnStyle}
                      href={`https://www.google.com/maps/dir/?api=1&destination=${
                        restCoords!.latitude
                      },${restCoords!.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleNavigate}
                    >
                      <FormattedMessage id="app.map.navigate" />
                    </a>
                  </div>
                )}
              </div>

              {/* Map */}
              <div style={{ position: 'relative' }}>
                <MapContainer
                  center={defaultCenter}
                  zoom={14}
                  style={{ height: '450px', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {/* Restaurant marker */}
                  {restCoords && (
                    <ClickZoomMarker
                      position={[restCoords.latitude, restCoords.longitude]}
                      icon={restaurantIcon}
                    >
                      <Popup>
                        <strong>
                          {company?.name ||
                            intl.formatMessage({
                              id: 'app.restaurant.name',
                            })}
                        </strong>
                        <br />
                        {company?.address}
                        {company?.postal && `, ${company.postal}`}
                        {company?.city && ` ${company.city}`}
                        {restCoords && (
                          <>
                            <br />
                            <br />
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${restCoords.latitude},${restCoords.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#96e16b', fontWeight: 600 }}
                            >
                              <FormattedMessage id="app.map.navigate" />
                            </a>
                          </>
                        )}
                      </Popup>
                    </ClickZoomMarker>
                  )}

                  {/* User location marker */}
                  {userCoords && (
                    <ClickZoomMarker
                      position={[userCoords.latitude, userCoords.longitude]}
                      icon={userIcon}
                    >
                      <Popup>
                        <FormattedMessage id="app.map.getLocation" />
                        {distanceText && (
                          <>
                            <br />
                            <FormattedMessage id="app.map.distance" />:{' '}
                            {distanceText}
                          </>
                        )}
                      </Popup>
                    </ClickZoomMarker>
                  )}

                  <MapBoundsUpdater
                    restCoords={restCoords}
                    userCoords={userCoords}
                  />
                </MapContainer>
              </div>
            </>
          ) : (
            /* Fallback when no restaurant coords */
            <div
              style={{
                height: '250px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: '#f9f9f9',
              }}
            >
              <p style={{ color: '#999' }}>
                <FormattedMessage id="app.map.locationUnavailable" />
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
